import { NavLink, useLocation } from 'react-router-dom';
import { Ic, type IconName } from '../components/Ic';
import { Logo } from '../components/Logo';
import { useMe, useScrapers, useSystemSummary } from '../api/queries';
import { useSocketConnected } from '../api/socket';
import { isAttentionWorthy } from '../lib/scraperState';

interface NavItem {
  label: string;
  to: string;
  icon: IconName;
  /** Extra path prefixes that also mark this tab active (modal routes). */
  alsoActive?: RegExp;
}

const NAV: NavItem[] = [
  { label: 'Board', to: '/', icon: 'grid', alsoActive: /^\/raffle\// },
  { label: 'Calendar', to: '/calendar', icon: 'calendar' },
  { label: 'Raffles', to: '/raffles', icon: 'database' },
  { label: 'Sources', to: '/scrapers', icon: 'box' },
  { label: 'Social', to: '/social', icon: 'globe' },
  { label: 'Ops', to: '/ops', icon: 'activity' },
];

/** Live-ness is two independent facts: our socket is up, and the worker
 *  heartbeat is fresh. Both must hold before claiming the engine is running. */
function EnginePill() {
  const connected = useSocketConnected();
  const { data: summary } = useSystemSummary();
  const workerAlive = summary?.worker.alive ?? false;
  if (connected && workerAlive) return <span className="pill green dot">running</span>;
  if (!connected) return <span className="pill yellow dot">reconnecting…</span>;
  return <span className="pill red dot">worker down</span>;
}

export function TopNav() {
  const { pathname } = useLocation();
  const { data: me } = useMe();
  const { data: scrapers } = useScrapers();
  const attention = scrapers?.filter(isAttentionWorthy).length ?? 0;

  const isActive = (item: NavItem) =>
    item.to === '/'
      ? pathname === '/' || !!item.alsoActive?.test(pathname)
      : pathname.startsWith(item.to) || !!item.alsoActive?.test(pathname);

  return (
    <header className="topnav">
      <NavLink to="/" className="topnav-brand" aria-label="Flux Raffles home">
        <Logo name="Raffles" sub="RAFFLE ENGINE" accentClass="teal" />
      </NavLink>

      <nav className="topnav-tabs" aria-label="Primary">
        {NAV.map((item) => {
          const Icon = Ic[item.icon];
          const active = isActive(item);
          return (
            <NavLink
              key={item.to}
              to={item.to}
              className={`topnav-tab ${active ? 'active' : ''}`}
              aria-current={active ? 'page' : undefined}
            >
              <Icon className="ico" />
              <span>{item.label}</span>
              {item.to === '/scrapers' && attention > 0 && (
                <span className="count alert" aria-label={`${attention} need attention`}>{attention}</span>
              )}
            </NavLink>
          );
        })}
      </nav>

      <div className="topnav-right">
        <EnginePill />
        <NavLink to="/add-scraper" className="btn ghost sm icon" title="Add scraper" aria-label="Add scraper">
          <Ic.plus className="ico" />
        </NavLink>
        <NavLink to="/settings" className="btn ghost sm icon" title="Settings" aria-label="Settings">
          <Ic.settings className="ico" />
        </NavLink>
        <div className="topnav-user" title={me?.dev_mode ? 'Dev mode' : (me?.role || 'viewer').replace('_', ' ')}>
          {me?.avatar ? (
            <img className="avatar" src={me.avatar} alt="" width={26} height={26} />
          ) : (
            <span className="avatar" aria-hidden />
          )}
          <span className="topnav-name">{me?.name ?? '—'}</span>
        </div>
        <a className="btn ghost sm icon" href="/logout" title="Sign out" aria-label="Sign out">
          <Ic.external className="ico" />
        </a>
      </div>
    </header>
  );
}
